import '../../../../../theme/partials/_amount.scss';

import {
  IonIcon,
  IonLabel,
  IonSpinner,
  useIonViewDidEnter,
} from '@ionic/react';
import classNames from 'classnames';
import { chevronForwardOutline } from 'ionicons/icons';
import {
  ReactNode, useCallback, useEffect, useLayoutEffect, useRef, useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';

import DashboardEmpty from '@/assets/imgs/dashboard-empty.svg';
import { OPERATION_DASHBOARD_DETAIL, TABLE_SCROLL_EVENT, TABLE_SCROLL_EVENT_BOTTOM } from '@/constants';
import useMediaQuery, { Breakpoint } from '@/hooks/useMediaQuery';
import { DashboardColumns, DashboardColumns as DashboardColumnsEnum } from '@/interfaces/DashboardInterface/DashboardInterface';
import { setColWidths } from '@/redux/dashboard/dashboard';
import { saveOperationData } from '@/redux/operation/operations';
import {
  Dashboard,
} from '@/redux/operation/operationsInterface';
import { storageService } from '@/services/storage';
import { RootState } from '@/store';
import { getCustomerFullName } from '@/utils/customerHelper';
import { getDefaultDashboardColumns } from '@/utils/dashboardColumnts';

import styles from './DashboardTableDesktop.module.scss';

interface DashboardTableDesktopProps {
  data: any;
  onChangeShowAGCode: (showAgCode: boolean) => void;
  loadingData: boolean;
  isRefreshing: boolean;
  onScrollEnd: () => void;
  moreItems: boolean;
  emptyMessage?: ReactNode;
  emptyMessageId?: string;
}

export interface DashboardColumnConfig {
  id: DashboardColumnsEnum;
  className?: string;
  sortable?: boolean;
}

const useElementVisibility = () => {
  const [isVisible, setIsVisible] = useState(false);
  const elementRef = useRef<HTMLTableRowElement>(null);

  const callback = useCallback((entries: IntersectionObserverEntry[]) => {
    const [entry] = entries;
    setIsVisible(entry.isIntersecting);
  }, []);

  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    const observer = new IntersectionObserver(callback, {
      root: null,
      rootMargin: '0px',
      threshold: 0.1,
    });

    observer.observe(element);

    return () => {
      if (element) {
        observer.unobserve(element);
      }
    };
  }, [callback]);

  return { elementRef, isVisible };
};

const TableRow = ({
  rowData,
  index,
  dashboardColumns,
  onRowVisibility,
  onRowClick,
  renderColumnData,
}: {
  rowData: Dashboard;
  index: number;
  dashboardColumns: DashboardColumnConfig[];
  onRowVisibility: (index: number, isVisible: boolean) => void;
  onRowClick: (data: any) => void;
  renderColumnData: (column: DashboardColumnsEnum, rowData: Dashboard) => React.ReactNode;
}) => {
  const { elementRef, isVisible } = useElementVisibility();
  useEffect(() => {
    onRowVisibility(index, isVisible);
  }, [isVisible, index, onRowVisibility]);

  return (
    <tr
      ref={elementRef}
      className={styles.generalTableRowData}
      onClick={() => onRowClick({
        application: rowData.application,
        merchant_id: rowData.merchant_id,
        product: rowData.product,
        business_status: rowData.business_status,
        order: rowData.order,
        proposal: rowData.proposal,
        loan: rowData.loan,
        customer: rowData.customer,
        created_date: rowData.created_date,
      })}
    >
      {dashboardColumns.map((column) => (
        <td key={column.id} className={column.className || ''}>
          {renderColumnData(column.id, rowData)}
        </td>
      ))}
    </tr>
  );
};

export default function DashboardTableDesktop({
  data,
  onChangeShowAGCode,
  loadingData,
  isRefreshing,
  onScrollEnd,
  moreItems,
  emptyMessage,
  emptyMessageId,
}: DashboardTableDesktopProps) {
  const history = useHistory();
  const dispatch = useDispatch();

  const scrollBottomRef = useRef<HTMLDivElement>(null);

  const isTablet = useMediaQuery(Breakpoint.Tablet);
  const isDesktop = useMediaQuery(Breakpoint.Tablet);
  const isDesktopLarge = useMediaQuery(Breakpoint.DesktopLarge);

  const [isMobile, setIsMobile] = useState(!isTablet && !isDesktop);
  const [dashboardColumns, setDashboardColumns] = useState<DashboardColumnConfig[]>([]);
  const customDashboardColumns = useSelector((state: RootState) => state.dashboard.columns);
  const scrollTopRef = useRef<HTMLDivElement>(null);
  const tableRef = useRef<HTMLTableElement>(null);
  const [cookieProposalReference, setCookieProposalReference] = useState(storageService.hasAGCode());

  useEffect(() => {
    const handleTableScroll = (event: CustomEvent) => {
      if (scrollBottomRef.current) {
        scrollBottomRef.current.scrollLeft = event.detail.scrollLeft;
      }
    };

    window.addEventListener(TABLE_SCROLL_EVENT, handleTableScroll as EventListener);

    return () => {
      window.removeEventListener(TABLE_SCROLL_EVENT, handleTableScroll as EventListener);
    };
  }, []);

  useEffect(() => {
    const newValue = storageService.hasAGCode();
    if (cookieProposalReference !== newValue) {
      setCookieProposalReference(newValue);
    }
  }, [storageService.hasAGCode()]);

  useEffect(() => {
    setIsMobile(!isTablet && !isDesktop);
    if (isDesktopLarge || isTablet) {
      setDashboardColumns(customDashboardColumns.map((col) => ({ id: col })));
      if (customDashboardColumns.includes(DashboardColumns.AG_CODE) || cookieProposalReference) {
        onChangeShowAGCode(true);
        storageService.setAGCode(true);
      } else {
        onChangeShowAGCode(false);
        storageService.setAGCode(false);
      }
    } else {
      setDashboardColumns(getDefaultDashboardColumns().map((col) => ({ id: col })));
    }
  }, [isTablet, isDesktop, isDesktopLarge, customDashboardColumns]);

  const getColWidths = () => {
    if (tableRef.current) {
      const firstRow = tableRef.current.querySelector('tbody tr');
      if (firstRow) {
        const tds = firstRow.querySelectorAll('td');
        const widths = Array.from(tds).map((td) => td.getBoundingClientRect().width);
        dispatch(setColWidths(widths));
      }
    }
  };

  useIonViewDidEnter(() => {
    getColWidths();
  });

  useLayoutEffect(() => {
    getColWidths();
  }, [dashboardColumns, data]);

  useEffect(() => {
    const handleResize = () => {
      if (tableRef.current) {
        getColWidths();
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const bottom = scrollBottomRef.current;
    if (!bottom) return;

    const handleWheel = () => {
      const customEvent = new CustomEvent(TABLE_SCROLL_EVENT_BOTTOM, {
        detail: { scrollLeft: scrollBottomRef.current?.scrollLeft },
      });
      window.dispatchEvent(customEvent);
    };

    bottom.addEventListener('scroll', handleWheel, { passive: false });

    return () => bottom.removeEventListener('scroll', handleWheel);
  }, []);

  useEffect(() => {
    const top = scrollTopRef.current;
    const bottom = scrollBottomRef.current;
    const table = tableRef.current;
    if (!top || !bottom || !table) return;

    getColWidths();
  }, [data, dashboardColumns.length]);

  const padNumber = (num: number): string => (num < 10 ? `0${num}` : `${num}`);

  const convertDateFormat = (inputDate: string): string => {
    const date = new Date(inputDate);
    return `${padNumber(date.getDate())}/${padNumber(
      date.getMonth() + 1,
    )}/${date.getFullYear()}`;
  };

  const handleClick = (value: any) => {
    const operationSelected = {
      data: value,
    };
    dispatch(saveOperationData(operationSelected));
    if (value.order) {
      history.push(`${OPERATION_DASHBOARD_DETAIL}/?cart_id=${value.order.cart_id}&order_id=${value.order.order_id}`);
    } else {
      history.push(`${OPERATION_DASHBOARD_DETAIL}/?credit_proposal_id=${value.proposal.credit_proposal_id}`);
    }
    // history.push(OPERATION_DASHBOARD_DETAIL);
  };

  const renderColumnData = (column: DashboardColumnsEnum, rowData: Dashboard) => {
    const {
      business_status_description,
      customer,
      created_date,
      order,
      proposal,
      application,
      product,
    } = rowData;
    switch (column) {
      case DashboardColumnsEnum.STATUS:
        return (
          <div className={classNames(styles.targetStatusCol, styles.dashboardInitialRow, isMobile || (!isDesktopLarge && !isTablet) ? styles.mobile : '')}>
            <IonLabel className={classNames(styles.targetStatusLabel, business_status_description)}>
              {business_status_description}
            </IonLabel>
            {((isMobile || (!isDesktopLarge && !isTablet)) && dashboardColumns.some((col) => col.id === DashboardColumnsEnum.AMOUNT)) ? (
              <div>
                <IonLabel className={classNames(styles.tableAmount, styles.tableAmountMobile, business_status_description ? `amount-${business_status_description}` : '')}>
                  {order?.total_price?.toLocaleString('de-DE', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  }) || proposal?.principal_amount?.toLocaleString('de-DE', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                  €
                </IonLabel>
                <IonIcon ios={chevronForwardOutline} className={styles.targetStatusIcon} />
              </div>
            ) : (
              <IonIcon ios={chevronForwardOutline} className={styles.targetStatusIcon} />
            )}
          </div>
        );

      case DashboardColumnsEnum.DNI_CIF:
        return (
          <div className={styles.documentId}>
            <IonLabel className={styles.columnTruncate}>{customer?.document_id}</IonLabel>
          </div>
        );

      case DashboardColumnsEnum.AG_CODE:
        return cookieProposalReference && (isDesktop || isDesktopLarge || isTablet) ? (
          <div>
            <IonLabel className={classNames(styles.proposalReferenceContent, styles.columnTruncate)}>
              {proposal?.proposal_reference && proposal.proposal_reference}
            </IonLabel>
          </div>
        ) : null;

      case DashboardColumnsEnum.CUSTOMER:
        if ((isMobile || (!isDesktopLarge && !isTablet)) || (!isMobile && (isDesktopLarge || isTablet))) {
          return (
            <div className={classNames(styles.nameSurname, isMobile || (!isDesktopLarge && !isTablet) ? styles.mobile : styles.clientName)}>
              <IonLabel className={styles.columnTruncate}>
                {getCustomerFullName(customer).toLocaleLowerCase()}
              </IonLabel>
            </div>
          );
        }
        return null;

      case DashboardColumnsEnum.DATE:
        return (
          <div className={styles.operationMobileRow}>
            <IonLabel className={classNames(styles.createdDate, styles.columnTruncate)}>
              {convertDateFormat(created_date)}
            </IonLabel>
          </div>
        );

      case DashboardColumnsEnum.AMOUNT:
        if (!isMobile && (isDesktopLarge || isTablet)) {
          return (
            <div className={classNames(styles.tableAmount, styles.columnTruncate, business_status_description ? `amount-${business_status_description}` : '')}>
              <IonLabel className={classNames(styles.tableAmount, styles.columnTruncate, business_status_description ? `amount-${business_status_description}` : '')}>
                {order?.total_price?.toLocaleString('de-DE', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                }) || proposal?.principal_amount?.toLocaleString('de-DE', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
                {' '}
                €
              </IonLabel>
            </div>
          );
        }
        return null;

      case DashboardColumnsEnum.EMAIL:
        return (
          <div className={styles.operationMobileRow}>
            <IonLabel className={classNames(styles.createdDate, styles.columnTruncate)}>
              {customer?.email}
            </IonLabel>
          </div>
        );

      case DashboardColumnsEnum.OFFER:
        return (
          <div className={styles.operationMobileRow}>
            <IonLabel className={classNames(styles.createdDate, styles.columnTruncate)}>
              {proposal?.commercial_offer_name}
            </IonLabel>
          </div>
        );

      case DashboardColumnsEnum.PROPOSAL_TERM:
        return (
          <div className={styles.operationMobileRow}>
            <IonLabel className={classNames(styles.createdDate, styles.columnTruncate)}>
              {proposal?.term}
            </IonLabel>
          </div>
        );
      case DashboardColumnsEnum.CART_ID:
        return (
          <div className={styles.operationMobileRow}>
            <IonLabel className={classNames(styles.createdDate, styles.columnTruncate)}>
              {order?.cart_id}
            </IonLabel>
          </div>
        );
      case DashboardColumnsEnum.PRODUCT:
        return (
          <div className={styles.operationMobileRow}>
            <IonLabel className={classNames(styles.createdDate, styles.columnTruncate)}>
              {product}
            </IonLabel>
          </div>
        );
      case DashboardColumnsEnum.APPLICATION:
        return (
          <div className={styles.operationMobileRow}>
            <IonLabel className={classNames(styles.createdDate, styles.columnTruncate)}>
              {application}
            </IonLabel>
          </div>
        );
      case DashboardColumnsEnum.PRODUCTS_NUM:
        return (
          <div className={styles.operationMobileRow}>
            <IonLabel className={classNames(styles.createdDate, styles.columnTruncate)}>
              {order?.total_products}
            </IonLabel>
          </div>
        );
      case DashboardColumnsEnum.PROCESS_TYPE:
        return (
          <div className={styles.operationMobileRow}>
            <IonLabel className={classNames(styles.createdDate, styles.columnTruncate)}>
              {order?.process_type}
            </IonLabel>
          </div>
        );

      default:
        return null;
    }
  };

  const handleRowVisibility = useCallback((index: number, isVisible: boolean) => {
    if (index === data.length - 1 && isVisible && moreItems) {
      onScrollEnd();
    }
  }, [data, moreItems]);

  return (
    <div className={styles.dashboardDesktopTableOuter}>
      <div
        className={styles.dashboardTableContainer}
        ref={scrollBottomRef}
      >
        <table className={styles.contentTableWrapper} ref={tableRef}>
          <tbody>
            {(data && data.length > 0) && (
              data.map((rowData: Dashboard, index: number) => (
                <TableRow
                  key={index}
                  rowData={rowData}
                  index={index}
                  dashboardColumns={dashboardColumns}
                  onRowVisibility={handleRowVisibility}
                  onRowClick={handleClick}
                  renderColumnData={renderColumnData}
                />
              ))
            )}
            {(!data || data.length === 0) && !loadingData && !isRefreshing && (
              <tr>
                <td colSpan={dashboardColumns.length}>
                  <div className={styles.noDataWrapper}>
                    <div className={styles.noDataTitle}>
                      <h1>¡Ups!</h1>
                      {emptyMessage ? (
                        <p id={emptyMessageId} role="alert">
                          {emptyMessage}
                        </p>
                      ) : (
                        <p>
                          Actualmente, no hay ninguna operación
                          <br />
                          {' '}
                          que coincida con tu búsqueda
                        </p>
                      )}
                    </div>
                    <img src={DashboardEmpty} alt="No data" />
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
        {loadingData && (
          <div className={styles.scrollSpinnerContainer}>
            <IonSpinner
              color="danger"
              name="bubbles"
              className={styles.scrollSpinner}
            />
          </div>
        )}
      </div>
    </div>
  );
}
